import React, { useEffect, useState, useRef, useCallback } from "react";
import { createCustomerSales, saveCustomSlabPlan } from "../services/sales";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../services/api/axios";
import { useUser } from "../context/UserContext";
import PropertyBuyerDetails from "./SaleSteps/PropertyBuyerDetails";
import SaleAgreementDetails from "./SaleSteps/SaleAgreementDetails";
import PaymentTable from "./SaleSteps/PaymentTable";
import PreviewConfirmation from "./SaleSteps/PreviewConfirmation";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  CheckCircle,
  Building2,
  FileText,
  Wallet,
  ClipboardCheck,
} from "lucide-react";
import { IconButton, OutlineButton } from "./Buttons";
import { FormModal } from "./form";

const SLAB_WISE_PAYMENT_TYPE_ID = 5;

// Step metadata drives both the stepper chrome and the body. Keeping it in one
// place means the visual order and the navigation logic can never drift apart.
const STEPS = [
  { id: 0, label: "Property & Buyer", sublabel: "Who's buying", Icon: Building2 },
  { id: 1, label: "Sale & Agreement", sublabel: "Pricing & terms", Icon: FileText },
  { id: 2, label: "Payment Plan", sublabel: "Schedule & token", Icon: Wallet },
  { id: 3, label: "Review & Confirm", sublabel: "Final check", Icon: ClipboardCheck },
];

const INITIAL_FORM = (initialAgentId) => ({
  customerId: "",
  secondaryUserIds: [],
  propertyId: "",
  saleDate: "",
  basePrice: "",
  stampDuty: "",
  gst: "",
  registrationPrice: "",
  currencyId: 0,
  customerBankEmail: "",
  paymentType: "",
  totalAmount: 0,
  agentId: initialAgentId || "",
  isBankLoanInvolved: false,
  bankName: "",
  branch: "",
  loanAmount: "",
  bankEmail: "",
  userState: "",
  customerState: "",
  source: "",
  presalesId: "",
  referrerUserId: "",
  referralRewardAmount: "",
});

const SaleDialog = ({
  isOpen,
  selectedStatus,
  onClose,
  property,
  onSaleSuccess,
  agentId: initialAgentId,
}) => {
  // Single source of truth for the wizard position. (Previously this lived in
  // two desynced states — `activeStep` and `currentStep` — which left the
  // PaymentTable "next" button and the Preview "edit" links silently dead.)
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { userInfo } = useUser();
  const navigate = useNavigate();

  const [formData, setFormData] = useState(() => INITIAL_FORM(initialAgentId));
  const [, setRemainingAmount] = useState(0);
  const [paymentInstallments, setPaymentInstallments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const toastRef = useRef(null);

  const handleTotalAmountChange = (amount) => {
    setFormData((prevData) => ({ ...prevData, totalAmount: amount }));
    setRemainingAmount(amount);
  };

  const handlePaymentOptionSelect = (option) => {
    setFormData((prev) => ({ ...prev, paymentType: option }));
  };

  const handlePaymentTableDataChange = (data) => {
    setPaymentInstallments(data);
  };

  const handleCustomerSelect = useCallback((customer) => {
    if (customer?.user?.address?.state) {
      setFormData((prev) => ({
        ...prev,
        customerState: customer.user.address.state,
      }));
    }
  }, []);

  const showRemainingAmountToast = (message) => {
    if (toastRef.current) {
      toast.warn(message, { position: toast.POSITION.TOP_CENTER });
    }
  };

  const createSale = async () => {
    try {
      setIsLoading(true);

      const salePayload = {
        customerId: userInfo.loginIdentifierDetails.customer.customerId,
        agentId: parseInt(formData.agentId) || null,
        presalesId: formData.presalesId ? parseInt(formData.presalesId) : null,
        propertyId: property.propertyId,
        userId: parseInt(formData.customerId),
        secondaryUsersId: (formData.secondaryUserIds || []).map(Number),
        loginId: userInfo.loginId,
        saleDate: formData.saleDate,
        basePrice: parseFloat(formData.basePrice),
        stampDuty: parseFloat(formData.stampDuty),
        gst: parseFloat(formData.gst),
        registrationPrice: parseFloat(formData.registrationPrice),
        currencyId: parseInt(formData.currencyId),
        customerBankEmail: formData.customerBankEmail,
        paymentOption: formData.paymentType,
        isLoan: formData.isBankLoanInvolved || false,
        source: formData.source,
        referrerUserId:
          formData.source === "REFERRAL" && formData.referrerUserId
            ? parseInt(formData.referrerUserId)
            : null,
        referralRewardAmount:
          formData.source === "REFERRAL" && formData.referralRewardAmount
            ? parseFloat(formData.referralRewardAmount)
            : null,
        customerBankDetails: formData.isBankLoanInvolved
          ? {
              bankName: formData.bankName || "",
              branchName: formData.branch || "",
              loanAmount: parseFloat(formData.loanAmount) || 0,
              bankEmail: formData.bankEmail || "",
            }
          : null,
      };

      const response = await createCustomerSales(salePayload);
      const salesId = response.customerSalesId;

      if (paymentInstallments && paymentInstallments.length > 0) {
        const installmentPayload = paymentInstallments.map((installment) => ({
          customerSalesId: salesId,
          projectedAmount: parseFloat(installment.projectedAmount) || 0,
          receivedAmount: 0,
          projectedDate: installment.projectedDate
            ? new Date(installment.projectedDate).toISOString().split("T")[0]
            : null,
          receivedDate: null,
          details: installment.details || "",
          paymentTypeId: installment.paymentTypeId || 0,
          // v2 (§D): installment→stage FK is wbsStageId (PaymentTable emits it on slab rows,
          // L646/L779). statusTypeId is dropped; no hardcoded stage-id fallback.
          wbsStageId: installment.wbsStageId ?? null,
          installmentBankAmount: installment.bankAmount ? parseFloat(installment.bankAmount) : 0,
          installmentUserAmount: installment.userAmount ? parseFloat(installment.userAmount) : 0,
          baseAmount: installment.baseAmount || 0,
          cgstAmount: installment.cgstAmount || 0,
          sgstAmount: installment.sgstAmount || 0,
          igstAmount: installment.igstAmount || 0,
        }));

        await axiosInstance.post(`/customer/sales/installment/bulk-insert`, installmentPayload);

        // Persist negotiated slab percentages so the Sale Detail page can show them.
        const slabInstallments = paymentInstallments.filter(
          (i) => i.paymentTypeId === SLAB_WISE_PAYMENT_TYPE_ID
        );
        if (slabInstallments.length > 0) {
          try {
            await saveCustomSlabPlan(salesId, slabInstallments);
          } catch (planError) {
            console.error("Error saving custom slab plan:", planError);
            toast.warning(
              "Sale and installments created, but custom slab plan could not be saved: " +
                (planError.response?.data?.message || planError.message)
            );
          }
        }
      }

      toast.success("Sale and installments created successfully!");
      if (onSaleSuccess) onSaleSuccess(salesId);
      navigate(
        `/properties/sold/${salesId}?projectId=${property.projectPhase.project.projectId}`,
        { state: { property, customerSaleId: salesId } }
      );
    } catch (error) {
      console.error("Error creating sale:", error);
      toast.error(
        "Error creating sale: " + (error.response?.data?.message || error.message)
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Per-step validation ────────────────────────────────────────────────
  const validateBuyerStep = () => {
    if (!formData.customerId) {
      toast.warning("Please select a buyer to continue.");
      return false;
    }
    return true;
  };

  const validateSaleAgreementDetails = () => {
    const requiredFields = ["saleDate", "basePrice", "currencyId", "totalAmount"];
    const missingFields = requiredFields.filter((field) => !formData[field]);
    if (missingFields.length > 0) {
      toast.error(
        `Please fill in all required fields in agreement details: ${missingFields.join(", ")}`
      );
      return false;
    }
    return true;
  };

  const validateForm = () => {
    const requiredFields = [
      "customerId",
      "source",
      "saleDate",
      "basePrice",
      "paymentType",
      "totalAmount",
    ];
    const missingFields = requiredFields.filter((field) => !formData[field]);
    if (missingFields.length > 0) {
      toast.warning(`Please fill in all required fields: ${missingFields.join(", ")}`);
      return false;
    }
    // A REFERRAL sale that credits a referrer must carry a positive reward amount.
    if (formData.source === "REFERRAL" && formData.referrerUserId) {
      const amount = parseFloat(formData.referralRewardAmount);
      if (!amount || amount <= 0) {
        toast.warning("Please enter a referral reward amount for the referred-by buyer.");
        return false;
      }
    }
    return true;
  };

  // Validate the current step before allowing forward movement.
  const canLeaveStep = (step) => {
    if (step === 0) return validateBuyerStep();
    if (step === 1) return validateSaleAgreementDetails();
    return true;
  };

  const goNext = () => {
    if (currentStep === STEPS.length - 1) {
      if (validateForm()) createSale();
      return;
    }
    if (!canLeaveStep(currentStep)) return;
    setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  // Stepper node click — only allow jumping to an already-completed step
  // (backward). Forward jumps must go through Next so validation runs.
  const handleStepClick = (index) => {
    if (index < currentStep) setCurrentStep(index);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const getCustomers = async () => {
      try {
        const response = await axiosInstance.get(`/customers/user`, {
          params: { customerId: userInfo.loginIdentifierDetails.customer.customerId },
        });
        const customersArray = response?.items || (Array.isArray(response) ? response : []);
        setCustomers(customersArray);
      } catch (error) {
        console.error("Error fetching customers:", error);
        setCustomers([]);
      }
    };
    getCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed the seller's state once user context is available.
  useEffect(() => {
    if (userInfo?.loginIdentifierDetails?.customer?.address?.state) {
      setFormData((prev) => ({
        ...prev,
        userState: userInfo.loginIdentifierDetails.customer.address.state,
      }));
    }
  }, [userInfo]);

  const resetForm = () => {
    setFormData(INITIAL_FORM(initialAgentId));
    setRemainingAmount(0);
    setPaymentInstallments([]);
    setCurrentStep(0);

    if (property?.propertyId) {
      localStorage.removeItem(`payment_data_${property.propertyId}`);
      localStorage.removeItem(`hybrid_payments_${property.propertyId}`);
    }
  };

  const stepBodies = [
    <PropertyBuyerDetails
      formData={formData}
      handleInputChange={handleInputChange}
      property={property}
      customers={customers}
      onCustomerSelect={handleCustomerSelect}
      userInfo={userInfo}
      fullProperty={property}
    />,
    <SaleAgreementDetails
      key={`sale-agreement-${paymentInstallments.length}`}
      formData={formData}
      handleInputChange={handleInputChange}
      onTotalAmountChange={handleTotalAmountChange}
      onPaymentOptionSelect={handlePaymentOptionSelect}
      property={property}
      paymentInstallments={paymentInstallments}
    />,
    <PaymentTable
      formData={formData}
      handleInputChange={handleInputChange}
      setFormData={setFormData}
      paymentType={formData.paymentType}
      onTotalAmountChange={handleTotalAmountChange}
      property={property}
      onPaymentTableDataChange={handlePaymentTableDataChange}
      showRemainingAmountToast={showRemainingAmountToast}
      onNextClick={goNext}
    />,
    <PreviewConfirmation
      formData={formData}
      customers={customers}
      property={property}
      setActiveStep={setCurrentStep}
    />,
  ];

  // Property context for the modal title — tells the seller exactly which unit
  // this sale is for, right in the header.
  const propertyNo = property?.propertyNo ?? property?.property_no;
  const projectName =
    property?.projectPhase?.project?.projectName ??
    property?.project_phase?.project?.project_name;
  const titleNode = (
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="text-lg font-semibold text-white">Create New Sale</span>
      </div>
      {(propertyNo || projectName) && (
        <p className="text-xs text-white/70 mt-0.5 truncate">
          {[projectName, propertyNo && `Unit ${propertyNo}`].filter(Boolean).join(" · ")}
        </p>
      )}
    </div>
  );

  const isLastStep = currentStep === STEPS.length - 1;

  return (
    <FormModal
      open={isOpen}
      onClose={() => {
        onClose();
        resetForm();
      }}
      title={titleNode}
      size="2xl"
      closeOnBackdrop={false}
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-medium text-slate-400 tabular-nums">
            Step {currentStep + 1} of {STEPS.length}
          </span>
          <div className="flex justify-end gap-3">
            {currentStep > 0 && (
              <OutlineButton
                className="w-32 inline-flex items-center justify-center gap-1.5"
                onClick={goBack}
                disabled={isLoading}
              >
                <ArrowLeft className="size-4" />
                Back
              </OutlineButton>
            )}
            <IconButton
              className={isLastStep ? "w-fit" : "w-32"}
              icon={
                isLastStep ? (
                  <CheckCircle className="mr-2 size-4" />
                ) : (
                  <ArrowRight className="ml-2 size-4" />
                )
              }
              left={isLastStep}
              onClick={goNext}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-transparent border-white" />
                  Creating Sale...
                </div>
              ) : isLastStep ? (
                "Confirm Sale"
              ) : (
                "Next"
              )}
            </IconButton>
          </div>
        </div>
      }
    >
      {/* ─── Fancy stepper ──────────────────────────────────────────────── */}
      <div className="-mx-6 -mt-5 mb-6 px-6 pt-5 pb-5 bg-gradient-to-b from-flamingo-50/70 to-white border-b border-flamingo-100/70">
        <div className="flex items-start">
          {STEPS.map((step, index) => {
            const isDone = index < currentStep;
            const isActive = index === currentStep;
            const StepIcon = step.Icon;
            const clickable = index < currentStep;
            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center text-center shrink-0 w-24">
                  <button
                    type="button"
                    onClick={() => handleStepClick(index)}
                    disabled={!clickable}
                    aria-label={step.label}
                    className={`relative flex h-11 w-11 items-center justify-center rounded-2xl transition-all duration-300 ${
                      clickable ? "cursor-pointer" : "cursor-default"
                    } ${
                      isActive
                        ? "bg-gradient-to-br from-flamingo-400 to-flamingo-600 text-white shadow-lg shadow-flamingo-200 ring-4 ring-flamingo-100 scale-105"
                        : isDone
                          ? "bg-flamingo-500 text-white shadow-sm hover:scale-105"
                          : "bg-white text-slate-400 border border-slate-200"
                    }`}
                  >
                    {isDone ? (
                      <Check size={20} strokeWidth={2.5} />
                    ) : (
                      <StepIcon size={18} strokeWidth={2} />
                    )}
                    {isActive && (
                      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-1.5 w-1.5 rounded-full bg-flamingo-500" />
                    )}
                  </button>
                  <p
                    className={`mt-2 text-[11px] font-semibold leading-tight transition-colors ${
                      isActive
                        ? "text-flamingo-700"
                        : isDone
                          ? "text-slate-700"
                          : "text-slate-400"
                    }`}
                  >
                    {step.label}
                  </p>
                  <p className="text-[10px] text-slate-400 leading-tight mt-0.5">
                    {step.sublabel}
                  </p>
                </div>
                {index < STEPS.length - 1 && (
                  <div className="flex-1 h-11 flex items-center px-1">
                    <div className="relative w-full h-1 rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-flamingo-400 to-flamingo-600 transition-all duration-500"
                        style={{ width: index < currentStep ? "100%" : "0%" }}
                      />
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step body */}
      <div>{stepBodies[currentStep]}</div>
    </FormModal>
  );
};

export default SaleDialog;
